import { type UseQueryLightError } from '@atlaskit/teams-client';

import { type HttpError } from '../model/errors';

export enum ErrorCategory {
	NotFound = 'NotFound',
	NotPermitted = 'NotPermitted',
	MalformedInput = 'MalformedInput',
	Internal = 'Internal',
}

export enum StatusCode {
	UNAUTHORIZED = 401,
	FORBIDDEN = 403,
	TIMEOUT = 408,
	GONE = 410,
	PAYLOAD_TOO_LARGE = 413,
}

export function isAuthError(error?: Error | HttpError) {
	return (
		isErrorStatusCode(StatusCode.UNAUTHORIZED, error) ||
		isErrorStatusCode(StatusCode.FORBIDDEN, error)
	);
}

export const IGNORED_ERROR_CONSTRUCTORS = ['SentryIgnoreError'];
export const IGNORED_ERROR_MESSAGES = {
	'Could not verify accountId from header': 'AccountIdMismatch',
};

export function ignoredErrorReason(error?: Error | HttpError): string | undefined {
	const constructor = error?.constructor.name;
	if (constructor && IGNORED_ERROR_CONSTRUCTORS.includes(constructor)) {
		return constructor;
	}
	const message = error?.message || '';
	const containedReason =
		message &&
		(Object.keys(IGNORED_ERROR_MESSAGES).find((ignoredMessage) =>
			message.includes(ignoredMessage),
		) as keyof typeof IGNORED_ERROR_MESSAGES);

	if (containedReason) {
		return IGNORED_ERROR_MESSAGES[containedReason];
	}
}

export function isErrorStatusCode(statusCode: number, error?: Error | HttpError): boolean {
	if (!error) {
		return false;
	}

	if (
		(error as any)['status'] === statusCode ||
		// error can be `ProperNetworkError` from `packages/graphql-client/src/links/error.ts`
		(error as any)['statusCode'] === statusCode
	) {
		return true;
	}

	const reg = new RegExp(`status (code)?.*(${statusCode})`);
	return !!error?.message?.match(reg);
}

export function is4xxError(error: Error | HttpError) {
	if (!error) {
		return false;
	}

	const statusCode = (error as any)['status'] || (error as any)['statusCode'];

	// We want to log 429 errors to understand rate limiting
	if (
		parseInt(statusCode, 10) >= 400 &&
		parseInt(statusCode, 10) < 500 &&
		parseInt(statusCode, 10) !== 429
	) {
		return true;
	}

	const reg = new RegExp(/status (code)?.*(4\d\d)/g);

	return !!error.message?.match(reg);
}

export function doesErrorContainStatus(statusCode: number, message: string) {
	const reg = new RegExp(`status .*(${statusCode})`);
	return reg.test(message);
}

export enum DirectoryErrorCode {
	MISSING_ERROR_MESSAGE = 'MissingErrorMessage',
}

export enum TeamErrorType {
	TEAM_NOT_ACTIVE = 'Team must be active',
}

export type ErrorObjectType = {
	code?: string;
	message?: TeamErrorType;
};

export interface RestJsonErrorResponse {
	errors: (TeamErrorType | ErrorObjectType)[];
	timestamp?: string;
}

export function errorHasProp<T>(error: unknown, field: string): error is Error & T {
	return error instanceof Error && field in (error as any);
}

export function isUseQueryLightError(err: unknown): err is UseQueryLightError {
	return typeof err === 'object' && err !== null && 'graphQLErrors' in err;
}
